import axios from 'axios'
import qs from 'qs'
// import router from '../router/index'
import { removeIsLogin } from '@/utils/auth';
import { Message, MessageBox } from 'element-ui';
import NProgress from 'nprogress';
import store from '../store'

// 常量定义
const CONSTANTS = {
    URL_PREFIX: '/td/api',
    DEFAULT_TIMEOUT: 30 * 1000,
    ERROR_MESSAGES: {
        SERVER_ERROR: '服务器错误',
        REQUEST_TIMEOUT: '请求超时',
        LOGIN_EXPIRED: '登录状态失效，请重新登录'
    },
    STATUS_CODES: {
        SUCCESS: 200,
        UNAUTHORIZED: 401,
        DISABLED: 407,
        SESSION_EXPIRED: 419,
        SERVER_ERROR: 500
    }
};

// 本地存储键名
const STORAGE_KEYS = {
    TIMEOUT_STATUS: 'timeoutStatus',
    DISABLED_STATUS: 'disabledStatus',
    TIMEOUT_TIPS_MODAL: 'timeoutTipsModal'
};

const axiosInstance = axios.create({
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    timeout: CONSTANTS.DEFAULT_TIMEOUT
});

let errMessage = null;
const requestMap = new Map();
// 请求拦截 请求拦截先不用
// ajax.interceptors.request.use(config => {
//     // config.headers.common['X-CSRF-TOKEN'] = getToken();
//     const list = ['/td/api/oppo/batch/preview', '/td/api/vivo/batch/preview', '/td/api/mi/batch/preview', '/td/api/data_analysis/plat/get_list'];
//     if (list.includes(config.url) ) {
//         config.timeout = 120 * 1000;
//     }
//     return config;
// });

/**
 * 清除登录相关的本地存储
 */
const clearLoginStorage = () => {
    Object.values(STORAGE_KEYS).forEach(key => {
        window.localStorage.removeItem(key);
    });
};

/**
 * 处理登出操作
 * @param {string} loginUrl - 登录页面URL
 */
const handleLogout = (loginUrl) => {
    store.commit('user/LOG_OUT');
    store.commit('permission/CLEAR_ROUTES');
    removeIsLogin();
    if (loginUrl) {
        window.location.href = loginUrl;
    }
};

/**
 * 处理会话过期
 * @param {Object} resData - 响应数据
 * @param {string} loginUrl - 登录页面URL
 */
const handleSessionExpired = (resData, loginUrl) => {
    const isShowTimeoutTipsModal = window.localStorage.getItem(STORAGE_KEYS.TIMEOUT_TIPS_MODAL);
    
    if (isShowTimeoutTipsModal === 'true') {
        return Promise.reject(resData);
    }

    MessageBox.confirm(CONSTANTS.ERROR_MESSAGES.LOGIN_EXPIRED, '提示', {
        closeOnClickModal: false,
        closeOnPressEscape: false,
        type: 'error',
        dangerouslyUseHTMLString: true,
        confirmButtonText: '重新登录',
        cancelButtonText: '关闭提示，稍后登录',
    }).then(() => {
        clearLoginStorage();
        handleLogout(loginUrl);
    }).catch(() => {
        window.localStorage.removeItem(STORAGE_KEYS.TIMEOUT_TIPS_MODAL);
        return Promise.reject(resData);
    });

    NProgress.done();
    removeIsLogin();
    window.localStorage.setItem(STORAGE_KEYS.TIMEOUT_STATUS, 'true');
    window.localStorage.setItem(STORAGE_KEYS.TIMEOUT_TIPS_MODAL, 'true');
};

// 响应拦截器
axiosInstance.interceptors.response.use(
    response => {
        if (!response.data || response.status !== CONSTANTS.STATUS_CODES.SUCCESS) {
            return Promise.reject(response);
        }

        const resData = response.data;

        // 处理成功响应
        if (resData.code === CONSTANTS.STATUS_CODES.SUCCESS) {
            clearLoginStorage();
            return Promise.resolve(resData);
        }

        // 处理UMC消息中心接口
        if (resData.error_code !== undefined) {
            return resData.error_code === 0 ? Promise.resolve(resData) : Promise.reject(resData);
        }

        const loginUrl = resData.data?.login_url;

        // 处理各种错误状态码
        switch (resData.code) {
            case CONSTANTS.STATUS_CODES.SESSION_EXPIRED:
                return handleSessionExpired(resData, loginUrl);
            
            case CONSTANTS.STATUS_CODES.UNAUTHORIZED:
            case CONSTANTS.STATUS_CODES.DISABLED:
                handleLogout(loginUrl);
                return Promise.reject(resData);
            
            case CONSTANTS.STATUS_CODES.SERVER_ERROR:
                Message.error(resData.info || CONSTANTS.ERROR_MESSAGES.SERVER_ERROR);
                return Promise.reject(resData);
            
            default:
                return Promise.reject(resData);
        }
    },
    error => {
        // 处理超时错误
        if (error.response?.status === 504 || error.message.includes('timeout') || error.stack?.includes('timeout')) {
            error.info = CONSTANTS.ERROR_MESSAGES.REQUEST_TIMEOUT;
        }
        
        return axios.isCancel(error) ? Promise.reject(null) : Promise.reject(error);
    }
);

/**
 * 创建取消令牌
 * @param {string} url - 请求URL
 * @param {Object} options - 请求配置
 * @returns {Object} 包含取消令牌的配置对象
 */
const createCancelTokenConfig = (url, options = {}) => {
    const requestKey = url;
    if (options.cancel && requestMap.has(requestKey)) {
        requestMap.get(requestKey)('cancel');
    }

    const source = axios.CancelToken.source();
    if (options.cancel) {
        requestMap.delete(requestKey);
        requestMap.set(requestKey, source.cancel);
    }

    return { source, requestKey };
};

// 导出请求方法
export const get = (url, data, options) => 
    axiosInstance.get(CONSTANTS.URL_PREFIX + url, { ...options, params: data });

export const post = (url, data, options) => 
    axiosInstance.post(CONSTANTS.URL_PREFIX + url, qs.stringify(data), options);

export const eGet = (url, data, options) => 
    axiosInstance.get(url, { ...options, params: data });

export const ePost = (url, data, options) => 
    axiosInstance.post(url, qs.stringify(data), options);

export const request = ({ url, ...params }) => 
    axiosInstance({ url: CONSTANTS.URL_PREFIX + url, ...params });

export const nGet = (url, data, options = { cancel: true }) => {
    const { source, requestKey } = createCancelTokenConfig(url, options);
    const config = {
        ...options,
        params: data,
        cancelToken: source.token
    };
    
    return axiosInstance.get(CONSTANTS.URL_PREFIX + url, config);
};

export const nPost = (url, data, options = { cancel: true }) => {
    const { source, requestKey } = createCancelTokenConfig(url, options);
    const config = {
        ...options,
        cancelToken: source.token
    };
    
    return axiosInstance.post(CONSTANTS.URL_PREFIX + url, qs.stringify(data), config);
};

export const postWithRetry = async (url, data, options) => {
    const retries = options.retries || '';
    const delay = options.delay;

    if (!retries) {
        return post(url, data, options);
    }

    try {
        const response = await axiosInstance.post(CONSTANTS.URL_PREFIX + url, qs.stringify(data), options);
        return response.data;
    } catch (error) {
        if (retries > 0 && (error.code === 'ECONNABORTED' || String(error).includes('504'))) {
            console.log(`Request timed out. Retrying... (${retries} attempts left)`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return postWithRetry(url, data, { ...options, retries: options.retries - 1 });
        }
        throw error;
    }
};
